import React, {
	forwardRef,
	useEffect,
	useRef,
	useState,
	useImperativeHandle,
	Ref,
} from 'react';
import cx from 'classnames';

import { ButtonPrimary } from '@/components/atoms/ButtonPrimary/render';

import {
	GdprCategory,
	GdprCategoryProps,
} from '@/components/molecules/Gdpr/GdprCategory/render';

import { getStyles } from './styles.css';

import block from '../block.json';

/**
 * TYPINGS
 */
type GdprModalProps = {
	categories: Array<GdprCategoryProps>;
	texts: object;
	onCategoryChange: Function;
	onModalSaved: Function;
	onModalClosed: Function;
	preview: boolean;
} & ThemeProps;

/**
 * COMPONENT
 */
export const GdprModal = forwardRef(
	(
		{
			categories,
			texts,
			onModalSaved,
			onModalClosed,
			onCategoryChange,
			preview = false,
			theme = {},
		}: GdprModalProps,
		ref: Ref<{}>
	) => {
		const [isHidden, setIsHidden] = useState(true);
		const elRef = useRef(null);
		const closeRef = useRef(null);
		const categoriesRef = useRef([]);
		const id = useRef(null);

		useEffect(() => {
			id.current = genId();
		}, []);

		// Allows to call these functions from the parent
		useImperativeHandle(ref, () => ({
			open,
			setCategoryEnabled,
		}));

		const bindEvents = () => {
			document.addEventListener('focusin', onLostFocus);
		};

		const unbindEvents = () => {
			document.removeEventListener('focusin', onLostFocus);
		};

		// ##############################
		// #region Event handler
		// ##############################

		const onSave = (event) => {
			event.stopPropagation();
			save();
		};

		const onClose = (event) => {
			event.stopPropagation();
			close();
		};

		const onLostFocus = (event) => {
			// On preview mode, avoid current focus on close button
			// Otherwise it affects the experience with the customizer
			if (elRef.current.contains(event.target) || preview) return;
			closeRef.current.focus();
		};

		// ##############################
		// #endregion
		// ##############################

		const open = () => {
			if (isHidden === false) return;

			setIsHidden(false);
			// On preview mode, avoid current focus on close button
			// Otherwise it affects the experience with the customizer
			if (!preview) {
				closeRef.current.focus();
			}

			bindEvents();
		};

		const save = () => {
			onModalSaved();
			close();
		};

		const close = () => {
			if (isHidden === true) return;

			setIsHidden(true);
			onModalClosed();

			unbindEvents();
		};

		const setCategoryEnabled = (category, enabled = false) => {
			categoriesRef.current
				.find((cat) => cat.getId() === category.id)
				.setEnabled(enabled);
		};

		/**
		 * Generate unique IDs for use as pseudo-private/protected names.
		 * Similar in concept to
		 * <http://wiki.ecmascript.org/doku.php?id=strawman:names>.
		 *
		 * The goals of this function are twofold:
		 *
		 * * Provide a way to generate a string guaranteed to be unique when compared
		 *   to other strings generated by this function.
		 * * Make the string complex enough that it is highly unlikely to be
		 *   accidentally duplicated by hand (this is key if you're using `ID`
		 *   as a private/protected name on an object).
		 *
		 * Use:
		 *
		 *     var privateName = ID();
		 *     var o = { 'public': 'foo' };
		 *     o[privateName] = 'bar';
		 *
		 * @source https://gist.github.com/gordonbrander/2230317;
		 */
		const genId = () => {
			// Math.random should be unique because of its seeding algorithm.
			// Convert it to base 36 (numbers + letters), and grab the first 9 characters
			// after the decimal.
			return '_' + Math.random().toString(36).substr(2, 9);
		};

		const rootClass = cx('supt-gdprModal', getStyles(theme));

		return (
			<section
				className={rootClass}
				role="dialog"
				aria-hidden={isHidden}
				tabIndex={-1}
				id={id.current}
				ref={elRef}
				data-block={block.slug}
			>
				<div
					className="supt-gdprModal__dialog"
					role="document"
					aria-labelledby="cookie-law-modal:title"
				>
					<div className="supt-gdprModal__content">
						<button
							className="supt-gdprModal__close"
							onClick={onClose}
							ref={closeRef}
						>
							<svg viewBox="-1 -1 18 18">
								<title>{texts['close']}</title>
								<line x1="16" y1="0" x2="0" y2="16" />
								<line
									x1="16"
									y1="16"
									x2="0"
									y2="4.4408921e-16"
								/>
							</svg>
						</button>
						<div className="supt-gdprModal__body">
							<header className="supt-gdprModal__head">
								<h3
									id="cookie-law-modal:title"
									className="supt-gdprModal__title"
								>
									{texts['title']}
								</h3>
								<p
									className="supt-gdprModal__desc"
									dangerouslySetInnerHTML={{
										__html: texts['description'],
									}}
								/>
							</header>
							<ul className="supt-gdprModal__categories">
								{categories.map((cat, index) => (
									<GdprCategory
										key={index}
										cat={cat}
										handleChange={onCategoryChange}
										ref={(categoryRef) =>
											(categoriesRef.current[index] =
												categoryRef)
										}
										theme={theme}
									/>
								))}
							</ul>
							<footer className="supt-gdprModal__footer">
								<ButtonPrimary
									title={texts['save']}
									className="supt-gdprModal__saveButton"
									onClick={onSave}
								/>
							</footer>
						</div>
					</div>
				</div>
			</section>
		);
	}
);
